import { useEffect, useRef, useState } from 'react';
import { useT } from '@platform/i18n';
import type { EyesOpenResult } from '../lib/eyeOpenness';
import type { FrameSize } from '../lib/framing';
import { projectBoxToCover } from '../lib/overlay';
import type { DetectedFace, GuidanceStatus } from '../types';
import { GuidanceBanner } from './GuidanceBanner';

interface CameraStageProps {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  cameraActive: boolean;
  guidance: GuidanceStatus;
  shutterReady: boolean;
  /** Seconds remaining in the auto-capture countdown, or null when not armed. */
  countdown: number | null;
  /** Live face boxes from the detector, in source-frame pixels. */
  faces: DetectedFace[];
  /** Source frame size the boxes are relative to. */
  frameSize: FrameSize | null;
  /** Eye-openness signals, for the dev tuning readout. */
  eyeOpenness: EyesOpenResult | null;
  /** True when the manual shutter was unlocked as an eye-gate fallback — show a hint. */
  manualFallback: boolean;
  onCapture: () => void;
}

/**
 * Full-bleed camera screen, iOS-camera style: live preview fills the stage,
 * the guidance pill floats near the bottom, and a circular shutter button
 * sits bottom-center. Capture is automatic (countdown on stable framing);
 * the shutter is the manual fallback — and the only path in the
 * detector-unavailable degraded mode, which must never auto-fire blind.
 * The preview is mirrored (selfie convention); the captured frame is not.
 */
export function CameraStage({
  videoRef,
  cameraActive,
  guidance,
  shutterReady,
  countdown,
  faces,
  frameSize,
  eyeOpenness,
  manualFallback,
  onCapture,
}: CameraStageProps) {
  const { t } = useT('face-capture');
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [viewport, setViewport] = useState<FrameSize | null>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () =>
      setViewport({ width: el.clientWidth, height: el.clientHeight });
    update();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const goodFraming = guidance === 'ready' || guidance === 'hold-still' || countdown !== null;

  return (
    <div ref={containerRef} className="relative h-full w-full overflow-hidden bg-black">
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        data-testid="camera-preview"
        className="absolute inset-0 h-full w-full -scale-x-100 object-cover"
      />

      {/* Live face-detection boxes, projected onto the mirrored cover-fit preview */}
      {frameSize &&
        viewport &&
        faces.map((face, i) => {
          const box = projectBoxToCover(face, frameSize, viewport);
          if (!box) return null;
          return (
            <div
              key={i}
              data-testid="face-box"
              aria-hidden
              className={`pointer-events-none absolute rounded-lg border-2 transition-all duration-150 ${
                goodFraming ? 'border-emerald-400' : 'border-amber-400'
              }`}
              style={{
                left: `${box.left}px`,
                top: `${box.top}px`,
                width: `${box.width}px`,
                height: `${box.height}px`,
              }}
            />
          );
        })}

      {countdown !== null && (
        <p
          data-testid="auto-capture-countdown"
          aria-hidden
          className="pointer-events-none absolute inset-0 flex items-center justify-center text-7xl font-bold text-white drop-shadow-lg"
        >
          {countdown}
        </p>
      )}

      {!cameraActive && (
        <p className="absolute inset-0 flex items-center justify-center text-sm text-white/80">
          {t('camera.starting')}
        </p>
      )}

      {/* Dev-only tuning readout: live gate signals.
          import.meta.env.DEV is a Vite built-in, allowed outside @config. */}
      {import.meta.env.DEV && (
        <p className="pointer-events-none absolute left-2 top-2 rounded bg-black/60 px-2 py-1 font-mono text-xs text-white">
          {guidance}
          {eyeOpenness
            ? ` · ear ${eyeOpenness.earMin.toFixed(2)} · blink ${eyeOpenness.blinkMax.toFixed(2)}`
            : ''}
        </p>
      )}

      {/* Bottom overlay: guidance pill above the iOS-style circular shutter */}
      <div className="absolute inset-x-0 bottom-0 flex flex-col items-center gap-4 bg-gradient-to-t from-black/60 to-transparent px-4 pb-6 pt-10">
        <GuidanceBanner status={countdown !== null ? 'hold-still' : guidance} />
        {manualFallback && countdown === null && (
          <p className="max-w-full text-center text-xs font-medium text-white/90">
            {t('camera.manualHint')}
          </p>
        )}
        <button
          type="button"
          aria-label={t('buttons.capture')}
          onClick={onCapture}
          disabled={!cameraActive || !shutterReady}
          className="group h-[68px] w-[68px] rounded-full border-4 border-white p-[3px] transition disabled:opacity-40"
        >
          <span className="block h-full w-full rounded-full bg-white transition group-enabled:group-active:scale-90" />
        </button>
      </div>
    </div>
  );
}
